import { splitIntoSentences } from "./paragraphUtils";

export const getContextForMode = (
  mode,
  state,
  index,
  generationMode = "open_ended",
  percentage_to_advance = 25
) => {
  switch (mode) {
    case "rewrite":
      return getRewriteContext(state, index);
    case "insert":
      return getInsertContext(state, index);
    case "new_paragraphs":
      return getNewParagraphsContext(
        state,
        generationMode,
        percentage_to_advance
      );
    case "new_paragraphs_suggestions":
      return getNewParagraphsSuggestionContext(state, percentage_to_advance);
    case "continue_scene":
      return getContinueSceneContext(state, index);
    default:
      return {};
  }
};

const getRewriteContext = (state, index) => {
  let next_paragraph = "";
  if (index + 1 < state.paragraphs.length) {
    next_paragraph = state.paragraphs[index + 1];
    if (next_paragraph.type === "section_break") {
      next_paragraph = "";
    }
  }
  let previous_paragraphs = state.paragraphs
    .slice(0, index)
    .filter((item) => !item.type);

  let previous_paragraphs_picked = previous_paragraphs.slice(-1).join("\n\n");

  const pickedSentencesText = getPickedSentencesFromParagraphs(
    state.paragraphs,
    state.pickedSentences
  );
  return {
    paragraph: state.paragraphs[index],
    premise: state.premise,
    previous_paragraph: previous_paragraphs_picked,
    next_paragraph: next_paragraph,
    picked_sentences: pickedSentencesText || "",
  };
};

const getPickedSentencesFromParagraphs = (
  section_break_ids,
  paragraphs,
  pickedSentencesMap
) => {
  if (!pickedSentencesMap) return "";

  // Initialize sections array with first section starting at 0
  const sections = [];
  let currentSection = {
    start: 0,
    end: section_break_ids[0] || paragraphs.length,
    sentences: [],
  };

  // Create a separate section for content after the last section break
  const lastSectionBreak = section_break_ids[section_break_ids.length - 1];
  let afterLastBreakSection = {
    start: lastSectionBreak,
    end: paragraphs.length,
    sentences: [],
  };

  // Sort paragraph indices to process them in order
  const paragraphIndices = Object.keys(pickedSentencesMap)
    .map((idx) => parseInt(idx))
    .sort((a, b) => a - b);

  // Group picked sentences into sections
  for (const paragraphIdx of paragraphIndices) {
    const sentenceIndices = pickedSentencesMap[paragraphIdx];
    const paragraph = paragraphs[paragraphIdx];
    if (!paragraph || paragraph.type === "section_break") continue;

    // If this paragraph is after the last section break, add it to the "current" section
    if (paragraphIdx > lastSectionBreak) {
      const sentences = splitIntoSentences(paragraph);
      sentenceIndices.forEach((sentenceIndex) => {
        if (sentences[sentenceIndex]) {
          afterLastBreakSection.sentences.push({
            text: sentences[sentenceIndex],
            paragraphIndex: paragraphIdx,
          });
        }
      });
      continue;
    }

    // Find which section this paragraph belongs to
    while (currentSection.end <= paragraphIdx && section_break_ids.length > 0) {
      if (currentSection.sentences.length > 0) {
        sections.push(currentSection);
      }
      const nextBreakIndex = section_break_ids.shift();
      currentSection = {
        start: currentSection.end,
        end: nextBreakIndex || paragraphs.length,
        sentences: [],
      };
    }

    // Add sentences from this paragraph to current section
    const sentences = splitIntoSentences(paragraph);
    sentenceIndices.forEach((sentenceIndex) => {
      if (sentences[sentenceIndex]) {
        currentSection.sentences.push({
          text: sentences[sentenceIndex],
          paragraphIndex: paragraphIdx,
        });
      }
    });
  }

  // Add the last regular section if it has any sentences
  if (currentSection.sentences.length > 0) {
    sections.push(currentSection);
  }

  // Filter out empty sections
  const nonEmptySections = sections.filter(
    (section) => section.sentences.length > 0
  );

  // Format the regular sections
  let formattedSections = nonEmptySections
    .map((section, sectionIndex) => {
      const sectionTitle = `Section ${sectionIndex + 1} (Paragraphs ${
        section.start + 1
      }-${section.end}):\n`;
      const sectionSentences = section.sentences
        .map(
          (sentence, idx) =>
            `  ${idx + 1}. [P${sentence.paragraphIndex + 1}] ${sentence.text}\n`
        )
        .join("");
      return sectionTitle + sectionSentences;
    })
    .join("\n");

  // Add the current section if it has any sentences
  if (afterLastBreakSection.sentences.length > 0) {
    if (formattedSections) formattedSections += "\n";
    formattedSections +=
      `Current Section (Paragraphs ${afterLastBreakSection.start + 1}-${
        afterLastBreakSection.end
      }):\n` +
      afterLastBreakSection.sentences
        .map(
          (sentence, idx) =>
            `  ${idx + 1}. [P${sentence.paragraphIndex + 1}] ${sentence.text}\n`
        )
        .join("");
  }

  return formattedSections;
};

const getInsertContext = (state, index) => {
  // console.log("getting insert context");
  let next_paragraph = "";
  if (index + 1 < state.paragraphs.length) {
    next_paragraph = state.paragraphs[index + 1];
    if (next_paragraph.type === "section_break") {
      next_paragraph = "";
    }
  }
  let previous_paragraphs = state.paragraphs
    .slice(0, index + 1)
    .filter((item) => !item.type);
  let previous_paragraphs_picked = previous_paragraphs.slice(-3).join("\n\n");

  let prev_sections = state.paragraphs
    .slice(0, index + 1)
    .filter((item) => item.type === "section_break")
    .map((item) => {
      const paragraphIndex = state.paragraphs.findIndex((p) => p === item);
      return {
        paragraph_id: paragraphIndex,
        text: item.summary || "",
      };
    });
  // console.log("prev_sections", prev_sections);

  let prev_sections_text = prev_sections
    .map(
      (section) =>
        `Section Summary (after paragraph ${section.paragraph_id}): ${section.text}`
    )
    .join("\n\n");
  // console.log("prev_sections_text", prev_sections_text);

  let section_break_ids = prev_sections.map((section) => section.paragraph_id);

  // Add picked sentences to context
  const prev_paragraphs_with_sections = state.paragraphs.slice(0, index + 1);
  let pickedSentencesText = getPickedSentencesFromParagraphs(
    section_break_ids,
    prev_paragraphs_with_sections,
    state.pickedSentences
  );

  // console.log(pickedSentencesText);

  // pickedSentencesText = "";

  return {
    premise: state.premise,
    previous_paragraphs: previous_paragraphs_picked,
    next_paragraph: next_paragraph,
    previous_sections: prev_sections_text,
    picked_sentences: pickedSentencesText || "",
  };
};

const getNewParagraphsContext = (
  state,
  generationMode = "open_ended",
  percentage_to_advance = 25
) => {
  const paragraphs = state.paragraphs.filter(
    (item) => item.type !== "section_break"
  );
  const previous_paragraphs = paragraphs.slice(-3).join("\n\n");
  let prev_sections = state.paragraphs
    .filter((item) => item.type === "section_break")
    .map((item) => item.summary);
  prev_sections = prev_sections.join("\n\n");
  let storyProgress = state.storyProgress || 0;
  if (storyProgress + percentage_to_advance > 100) {
    percentage_to_advance = 100 - storyProgress;
  }

  // Add picked sentences to context
  const pickedSentencesText = getPickedSentencesFromParagraphs(
    state.paragraphs,
    state.pickedSentences
  );

  if (generationMode === "percentage_based") {
    return {
      premise: state.premise,
      genre: state.genre,
      previous_paragraphs: previous_paragraphs,
      previous_sections: prev_sections,
      current_percentage: storyProgress || 0,
      target_percentage: storyProgress + percentage_to_advance,
      percentage_to_advance: percentage_to_advance,
      overall_summary: state.overall_summary,
      picked_sentences: pickedSentencesText || "",
    };
  } else if (
    generationMode === "open_ended" ||
    generationMode === "open_ended_dialogue"
  ) {
    return {
      premise: state.premise,
      genre: state.genre,
      previous_paragraphs: previous_paragraphs,
      previous_sections: prev_sections,
      picked_sentences: pickedSentencesText || "",
    };
  }
};

const getNewParagraphsSuggestionContext = (state, percentage_to_advance) => {
  const paragraphs = state.paragraphs.filter(
    (item) => item.type !== "section_break"
  );
  const previous_paragraphs = paragraphs.slice(-3).join("\n\n");
  return {
    premise: state.premise,
    genre: state.genre,
    previous_paragraphs: previous_paragraphs,
  };
};

const getContinueSceneContext = (state, index) => {
  return {
    premise: state.premise,
    previous_scene_elements: state.elements,
  };
};
